import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Calculator, RefreshCw, Settings, FileSpreadsheet, Wallet,
  ChevronLeft, ChevronRight, Pencil, X, Plus, Trash2, Save,
} from "lucide-react";
import { toast } from "sonner";
import { usePayrollSlips, SlipHeaderButtons, SlipBanner, SlipCell } from "@/components/PayrollSlips";

/** v84.94 — Payroll Bulanan (2 siklus per bulan).
 *  Siklus 1: 26 bln lalu → 8 bln ini.  Siklus 2: 9 → 25 bln ini.
 *  Komisi + Bonus Absensi (hari hadir × master value) + Gaji Tetap Staff (siklus 2)
 *  − Kasbon − Deductions + Additions.
 */
const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
const rpShort = (n) => (Number(n) || 0).toLocaleString("id-ID");

function currentCyclePeriod() {
  // Siklus 1 = tgl 26 bln lalu → 8 bln ini (period_key = YYYY-MM-C1, bulan=bulan ini)
  // Siklus 2 = tgl 9 → 25 bln ini
  // Jika tgl 26+ → C1 bulan DEPAN.
  const n = new Date();
  const d = n.getDate();
  let y = n.getFullYear();
  let m = n.getMonth() + 1;
  if (d >= 9 && d <= 25) {
    return { year: y, month: m, cycleNum: 2 };
  }
  if (d >= 26) {
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return { year: y, month: m, cycleNum: 1 };
}

export default function Payroll() {
  const _init = currentCyclePeriod();
  const [year, setYear]   = useState(_init.year);
  const [month, setMonth] = useState(_init.month);
  const [cycleNum, setCycleNum] = useState(_init.cycleNum);   // 1 or 2
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideLine, setOverrideLine] = useState(null);

  const periodKey = `${year}-${String(month).padStart(2, "0")}-C${cycleNum}`;
  const slips = usePayrollSlips(year, month);

  const latestReq = useRef(0);
  const load = useCallback(async () => {
    const req = ++latestReq.current;
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.payrollSettings(),
        api.payrollCalc(periodKey),
      ]);
      if (req !== latestReq.current) return;
      setSettings(s);
      setData(d);
    } catch (e) {
      toast.error("Gagal muat: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [periodKey]);

  useEffect(() => { load(); }, [load]);

  const period = data?.period;
  const lines  = data?.lines || [];
  const totals = data?.totals || {};

  function prevPeriod() {
    if (cycleNum === 2) { setCycleNum(1); return; }
    // cyc=1 → mundur ke bulan lalu cyc=2
    let y = year, m = month - 1;
    if (m < 1) { m = 12; y -= 1; }
    setYear(y); setMonth(m); setCycleNum(2);
  }
  function nextPeriod() {
    if (cycleNum === 1) { setCycleNum(2); return; }
    let y = year, m = month + 1;
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m); setCycleNum(1);
  }

  // v84.102 — Export XLSX (bukan CSV lagi). Backend generate file dgn format
  // template user (2 siklus, per-hari, pengurangan+tambahan+net). Admin dump
  // tiap tanggal 1 ke Drive sbg backup payroll.
  async function exportXlsx() {
    const backend = process.env.REACT_APP_BACKEND_URL;
    try {
      const resp = await fetch(
        `${backend}/api/payroll/export-xlsx?year=${year}&month=${month}`,
        { credentials: "include" }
      );
      if (!resp.ok) {
        const err = await resp.text();
        toast.error("Gagal export: " + err.slice(0, 120));
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payroll_${year}_${String(month).padStart(2,"0")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Payroll xlsx ter-download");
    } catch (e) {
      toast.error("Gagal export: " + e.message);
    }
  }

  return (
    <div className="space-y-4" data-testid="payroll-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-700" />
            Payroll
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            2 siklus/bulan: <b>Siklus 1</b> (26 bln lalu → 8 bln ini) & <b>Siklus 2</b> (9 → 25 bln ini).
            Komisi auto-tarik dari <b>Rekap Penagihan</b>, Kasbon dari input teknisi, Bonus Absensi dihitung dari hari hadir.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SlipHeaderButtons year={year} month={month} data={slips.data} reload={slips.reload} />
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}
            className="h-9 rounded-sm gap-1.5" data-testid="payroll-settings-btn">
            <Settings className="w-3.5 h-3.5" /> Master Data
          </Button>
          <Button variant="outline" size="sm" onClick={exportXlsx}
            className="h-9 rounded-sm gap-1.5" data-testid="payroll-export-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Export XLSX
          </Button>
          <Button variant="outline" size="sm" onClick={() => { load(); slips.reload(); }} disabled={loading}
            className="h-9 rounded-sm gap-1.5" data-testid="payroll-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Period Nav */}
      <div className="border border-zinc-200 rounded-sm bg-white p-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={prevPeriod}
              className="h-9 rounded-sm w-9 p-0" data-testid="payroll-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 border border-zinc-300 rounded-sm px-2 text-[12px] font-mono bg-white flex-1"
              data-testid="payroll-month-select">
              {Array.from({length:12}, (_,i)=>i+1).map(m => (
                <option key={m} value={m}>{String(m).padStart(2,"0")}</option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 border border-zinc-300 rounded-sm px-2 text-[12px] font-mono bg-white w-24"
              data-testid="payroll-year-select">
              {[year-1, year, year+1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={nextPeriod}
              className="h-9 rounded-sm w-9 p-0" data-testid="payroll-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex gap-1">
            <Button
              variant={cycleNum===1 ? "default" : "outline"} size="sm"
              onClick={() => setCycleNum(1)}
              className={`h-9 rounded-sm flex-1 text-[12px] ${cycleNum===1 ? "bg-emerald-700 hover:bg-emerald-800" : ""}`}
              data-testid="payroll-cycle-1-btn">
              Siklus 1 (26 → 8)
            </Button>
            <Button
              variant={cycleNum===2 ? "default" : "outline"} size="sm"
              onClick={() => setCycleNum(2)}
              className={`h-9 rounded-sm flex-1 text-[12px] ${cycleNum===2 ? "bg-emerald-700 hover:bg-emerald-800" : ""}`}
              data-testid="payroll-cycle-2-btn">
              Siklus 2 (9 → 25)
            </Button>
          </div>

          <div className="border-2 border-emerald-700 bg-emerald-50 rounded-sm p-2">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1">
              <Calculator className="w-3 h-3" /> Periode Aktif
            </div>
            <div className="text-[12px] font-mono text-emerald-900 mt-0.5" data-testid="payroll-period-range">
              {period?.date_from || "-"} → {period?.date_to || "-"}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <SummaryCard label="Komisi"    value={totals.komisi}         color="text-emerald-700" testid="sum-komisi"/>
        <SummaryCard label="Bonus"     value={totals.bonus_absensi}  color="text-blue-700"    testid="sum-bonus"/>
        <SummaryCard label="Gaji Tetap"value={totals.gaji_tetap}     color="text-indigo-700"  testid="sum-gaji"/>
        <SummaryCard label="Additions" value={totals.additions}      color="text-purple-700"  testid="sum-add"/>
        <SummaryCard label="Kasbon"    value={totals.kasbon}         color="text-orange-700"  neg testid="sum-kasbon"/>
        <SummaryCard label="Deductions"value={totals.deductions}     color="text-red-700"     neg testid="sum-ded"/>
      </div>

      <div className="border-2 border-emerald-700 bg-emerald-50 rounded-sm px-4 py-2 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
          Total Net Payroll {periodKey}
        </div>
        <div className="text-2xl font-bold text-emerald-700 font-mono" data-testid="payroll-net-total">
          {rp(totals.net || 0)}
        </div>
      </div>

      <SlipBanner year={year} month={month} data={slips.data} />

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-zinc-400">
            <RefreshCw className="w-6 h-6 animate-spin inline mr-2" /> Memuat…
          </div>
        ) : lines.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">
            Belum ada personel aktif.
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-zinc-50 sticky top-0">
              <TableRow>
                <TableHead className="min-w-[140px]">Nama</TableHead>
                <TableHead className="text-center min-w-[60px]">Hari</TableHead>
                <TableHead className="text-right min-w-[110px]">Komisi</TableHead>
                <TableHead className="text-right min-w-[110px]">Bonus</TableHead>
                <TableHead className="text-right min-w-[110px]">Gaji Tetap</TableHead>
                <TableHead className="text-right min-w-[110px]">Additions</TableHead>
                <TableHead className="text-right min-w-[110px] text-orange-700">Kasbon</TableHead>
                <TableHead className="text-right min-w-[110px] text-red-700">Deductions</TableHead>
                <TableHead className="text-right min-w-[130px] bg-emerald-50 text-emerald-800">NET</TableHead>
                <TableHead className="text-center min-w-[190px]">Slip</TableHead>
                <TableHead className="text-center w-16">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map(l => (
                <TableRow key={l.personnel_id} data-testid={`payroll-row-${l.personnel_id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1.5">
                      <span>{l.name}</span>
                      {l.is_staff && (
                        <span className="text-[9px] px-1 py-0.5 bg-indigo-100 text-indigo-700 rounded-sm border border-indigo-300 uppercase tracking-wider">Staff</span>
                      )}
                      {!l.active && (
                        <span className="text-[9px] px-1 py-0.5 bg-zinc-100 text-zinc-500 rounded-sm border border-zinc-300 uppercase tracking-wider">Inactive</span>
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">
                      {l.commission_percentage}% komisi
                    </div>
                  </TableCell>
                  <TableCell className="text-center font-mono text-[12px]">{l.hari_hadir}</TableCell>
                  <ValueCell val={l.komisi}       overridden={l.override_flags?.komisi}     color="text-emerald-700"/>
                  <ValueCell val={l.bonus_absensi} overridden={l.override_flags?.bonus}      color="text-blue-700"/>
                  <ValueCell val={l.gaji_tetap}    overridden={l.override_flags?.gaji_tetap} color="text-indigo-700"/>
                  <TableCell className="text-right font-mono text-[12px] text-purple-700">
                    {l.sum_additions > 0 ? rpShort(l.sum_additions) : <span className="text-zinc-300">—</span>}
                  </TableCell>
                  <ValueCell val={l.kasbon} overridden={l.override_flags?.kasbon} color="text-orange-700" neg/>
                  <TableCell className="text-right font-mono text-[12px] text-red-700">
                    {l.sum_deductions > 0 ? "-"+rpShort(l.sum_deductions) : <span className="text-zinc-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[13px] font-bold text-emerald-800 bg-emerald-50/60"
                    data-testid={`payroll-net-${l.personnel_id}`}>
                    {rp(l.net)}
                  </TableCell>
                  <SlipCell pid={l.personnel_id} slip={slips.byPid[l.personnel_id]} reload={slips.reload} />
                  <TableCell className="text-center">
                    <Button variant="ghost" size="sm"
                      className="h-7 w-7 p-0 rounded-sm"
                      onClick={() => { setOverrideLine(l); setOverrideOpen(true); }}
                      data-testid={`payroll-edit-${l.personnel_id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {/* Totals row */}
              <TableRow className="bg-emerald-50 font-semibold sticky bottom-0">
                <TableCell className="text-[11px] uppercase tracking-widest text-emerald-800">TOTAL</TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right font-mono text-emerald-700">{rpShort(totals.komisi || 0)}</TableCell>
                <TableCell className="text-right font-mono text-blue-700">{rpShort(totals.bonus_absensi || 0)}</TableCell>
                <TableCell className="text-right font-mono text-indigo-700">{rpShort(totals.gaji_tetap || 0)}</TableCell>
                <TableCell className="text-right font-mono text-purple-700">{rpShort(totals.additions || 0)}</TableCell>
                <TableCell className="text-right font-mono text-orange-700">-{rpShort(totals.kasbon || 0)}</TableCell>
                <TableCell className="text-right font-mono text-red-700">-{rpShort(totals.deductions || 0)}</TableCell>
                <TableCell className="text-right font-mono text-[13px] font-bold text-emerald-800 bg-emerald-100"
                  data-testid="payroll-total-net-row">
                  {rp(totals.net || 0)}
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </div>

      <div className="text-[11px] text-zinc-500 border-l-2 border-emerald-300 pl-3 py-1 space-y-1">
        <div>💡 <b>Bonus Absensi</b> = hari hadir × <b>{rp(settings?.bonus_absensi_per_hari)}</b> (edit di Master Data).</div>
        <div>💡 <b>Kasbon</b> auto-tarik dari input teknisi (halaman Rekap Kerja). Kalau 1 armada di-assign ke 2 orang, kasbon di-split rata.</div>
        <div>💡 <b>Gaji Tetap Staff</b> hanya dibayarkan di <b>Siklus 2</b>. Nilai override ditandai <span className="text-emerald-700">•</span>.</div>
      </div>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        personnel={lines}
        onSaved={(s) => { setSettings(s); load(); }}
      />

      <OverrideDialog
        open={overrideOpen}
        onClose={() => { setOverrideOpen(false); setOverrideLine(null); }}
        line={overrideLine}
        periodKey={periodKey}
        onSaved={load}
      />
    </div>
  );
}

function SummaryCard({ label, value, color, neg, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm bg-white p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className={`text-[15px] font-mono font-bold ${color} mt-0.5`}>
        {neg && (value||0) > 0 ? "-" : ""}{rpShort(value || 0)}
      </div>
    </div>
  );
}

function ValueCell({ val, overridden, color, neg }) {
  return (
    <TableCell className={`text-right font-mono text-[12px] ${color}`}>
      {(val || 0) > 0 ? (
        <span>
          {neg ? "-" : ""}{rpShort(val)}
          {overridden && <span className="ml-1 text-emerald-600" title="Override manual">•</span>}
        </span>
      ) : (
        <span className="text-zinc-300">—</span>
      )}
    </TableCell>
  );
}

// ── SETTINGS DIALOG ──────────────────────────────────────────────────
function SettingsDialog({ open, onClose, settings, personnel, onSaved }) {
  const [bonus, setBonus] = useState("");
  const [staffMap, setStaffMap] = useState({}); // {pid: {enabled, salary}}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !settings) return;
    setBonus(String(settings.bonus_absensi_per_hari || 25000));
    const sm = {};
    (personnel || []).forEach(p => {
      const cur = (settings.staff_fixed_salaries || {})[p.personnel_id];
      sm[p.personnel_id] = {
        name: p.name,
        enabled: cur !== undefined,
        salary: cur !== undefined ? String(cur) : "",
      };
    });
    setStaffMap(sm);
  }, [open, settings, personnel]);

  async function save() {
    setSaving(true);
    try {
      const salaries = {};
      Object.entries(staffMap).forEach(([pid, v]) => {
        if (v.enabled) {
          const n = Number(v.salary);
          if (n > 0) salaries[pid] = n;
        }
      });
      const r = await api.updatePayrollSettings({
        bonus_absensi_per_hari: Number(bonus || 25000),
        staff_fixed_salaries: salaries,
      });
      toast.success("Master data disimpan");
      onSaved?.(r);
      onClose();
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-4 h-4"/> Master Data Payroll
          </DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500">
            Atur bonus absensi per hari dan gaji tetap untuk staff.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
              Bonus Absensi per Hari Hadir
            </label>
            <Input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)}
              placeholder="25000" className="rounded-sm h-9 font-mono mt-1"
              data-testid="settings-bonus-input"/>
            <div className="text-[10px] text-zinc-500 mt-1">
              Default: Rp 25.000/hari. Bonus dihitung: hari hadir dalam siklus × nilai ini.
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
              Gaji Tetap Staff (dibayarkan di Siklus 2)
            </label>
            <div className="mt-1 border border-zinc-200 rounded-sm max-h-64 overflow-y-auto">
              {Object.entries(staffMap).length === 0 ? (
                <div className="p-3 text-[11px] text-zinc-400 text-center">Belum ada personel.</div>
              ) : Object.entries(staffMap).map(([pid, v]) => (
                <div key={pid} className="flex items-center gap-2 p-2 border-b border-zinc-100 last:border-0"
                  data-testid={`settings-staff-row-${pid}`}>
                  <Checkbox
                    checked={v.enabled}
                    onCheckedChange={(c) => setStaffMap(m => ({...m, [pid]: {...m[pid], enabled: !!c}}))}
                    data-testid={`settings-staff-check-${pid}`}
                  />
                  <span className="text-[12px] flex-1 truncate">{v.name}</span>
                  <Input type="number" disabled={!v.enabled}
                    value={v.salary}
                    onChange={(e) => setStaffMap(m => ({...m, [pid]: {...m[pid], salary: e.target.value}}))}
                    placeholder="Gaji bulanan"
                    className="h-8 rounded-sm font-mono text-[11px] w-36"
                    data-testid={`settings-staff-input-${pid}`}/>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">
              Centang personel yg dapat gaji tetap. Gaji hanya di-payout di <b>Siklus 2</b>.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Batal</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-emerald-700 hover:bg-emerald-800"
            data-testid="settings-save-btn">
            <Save className="w-3.5 h-3.5 mr-1"/> {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── OVERRIDE DIALOG (per-line manual edit) ───────────────────────────
function OverrideDialog({ open, onClose, line, periodKey, onSaved }) {
  const [additions, setAdditions] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [ovKomisi, setOvKomisi] = useState("");
  const [ovBonus, setOvBonus] = useState("");
  const [ovKasbon, setOvKasbon] = useState("");
  const [ovGaji, setOvGaji] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !line) return;
    setAdditions((line.additions || []).map(a => ({...a, amount: String(a.amount)})));
    setDeductions((line.deductions || []).map(d => ({...d, amount: String(d.amount)})));
    setOvKomisi(line.override_flags?.komisi     ? String(line.komisi)        : "");
    setOvBonus (line.override_flags?.bonus      ? String(line.bonus_absensi) : "");
    setOvKasbon(line.override_flags?.kasbon     ? String(line.kasbon)        : "");
    setOvGaji  (line.override_flags?.gaji_tetap ? String(line.gaji_tetap)    : "");
    setNote(line.note || "");
  }, [open, line]);

  async function save() {
    if (!line) return;
    setSaving(true);
    try {
      const clean = (arr) => arr
        .map(x => ({ label: (x.label || "").trim(), amount: Number(x.amount || 0) }))
        .filter(x => x.label && x.amount !== 0);
      await api.upsertPayrollOverride({
        period_key: periodKey,
        personnel_id: line.personnel_id,
        additions: clean(additions),
        deductions: clean(deductions),
        override_komisi:     ovKomisi === "" ? null : Number(ovKomisi),
        override_bonus:      ovBonus  === "" ? null : Number(ovBonus),
        override_kasbon:     ovKasbon === "" ? null : Number(ovKasbon),
        override_gaji_tetap: ovGaji   === "" ? null : Number(ovGaji),
        note,
      });
      toast.success("Override tersimpan");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  if (!line) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4"/> Edit Payroll — {line.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500">
            Override nilai auto-calc atau tambahkan komponen tambahan/potongan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <OverrideField label="Komisi"      calc={line.komisi_calc}     value={ovKomisi} onChange={setOvKomisi} testid="ov-komisi"/>
            <OverrideField label="Bonus Absensi" calc={line.bonus_calc}    value={ovBonus}  onChange={setOvBonus}  testid="ov-bonus"/>
            <OverrideField label="Kasbon"      calc={line.kasbon_calc}     value={ovKasbon} onChange={setOvKasbon} testid="ov-kasbon"/>
            <OverrideField label="Gaji Tetap"  calc={line.gaji_tetap_calc} value={ovGaji}   onChange={setOvGaji}   testid="ov-gaji"/>
          </div>
          <ItemList
            label="Additions (Tambahan)" color="purple"
            items={additions} setItems={setAdditions} testidPrefix="ov-add"
            options={["Bonus kehadiran", "Bonus absensi", "Komisi", "Lain-lain"]}/>
          <ItemList
            label="Deductions (Potongan)" color="red"
            items={deductions} setItems={setDeductions} testidPrefix="ov-ded"
            options={["Kasbon", "Simpanan", "Angsuran Pinjaman", "Asuransi", "PPH 21", "Lain-lain"]}/>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Catatan</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Catatan internal"
              className="rounded-sm h-9 mt-1 text-[12px]"
              data-testid="ov-note-input"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Batal</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-emerald-700 hover:bg-emerald-800"
            data-testid="ov-save-btn">
            <Save className="w-3.5 h-3.5 mr-1"/> {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideField({ label, calc, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        {label}
        <span className="ml-1 text-zinc-400 lowercase tracking-normal font-normal font-mono">
          (auto: {rp(calc)})
        </span>
      </label>
      <Input type="number" value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Kosongkan = pakai auto"
        className="rounded-sm h-9 mt-1 font-mono text-[12px]"
        data-testid={testid}/>
    </div>
  );
}

function ItemList({ label, color, items, setItems, testidPrefix, options }) {
  function add() {
    setItems([...items, { label: "", amount: "" }]);
  }
  function update(idx, key, val) {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [key]: val };
    setItems(copy);
  }
  function remove(idx) {
    setItems(items.filter((_, i) => i !== idx));
  }
  const colorClass = color === "purple"
    ? "border-purple-200 text-purple-700"
    : "border-red-200 text-red-700";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className={`text-[11px] uppercase tracking-widest font-semibold ${color==="purple"?"text-purple-700":"text-red-700"}`}>
          {label}
        </label>
        <Button variant="outline" size="sm" onClick={add}
          className="h-7 rounded-sm gap-1 text-[11px]"
          data-testid={`${testidPrefix}-add-btn`}>
          <Plus className="w-3 h-3"/> Tambah
        </Button>
      </div>
      <div className={`border rounded-sm ${colorClass} bg-white`}>
        {items.length === 0 ? (
          <div className="p-2 text-[11px] text-zinc-400 text-center">Kosong.</div>
        ) : items.map((it, i) => (
          <div key={i} className="flex items-center gap-1 p-1.5 border-b last:border-0 border-zinc-100"
            data-testid={`${testidPrefix}-row-${i}`}>
            {/* v84.189 — Select dropdown menggantikan Input bebas */}
            <div className="flex-1 min-w-0">
              <Select value={it.label || ""} onValueChange={(v) => update(i, "label", v)}>
                <SelectTrigger
                  className="rounded-sm h-8 text-[11px] w-full"
                  data-testid={`${testidPrefix}-label-${i}`}>
                  <SelectValue placeholder="Pilih kategori"/>
                </SelectTrigger>
                <SelectContent>
                  {(options || []).map(opt => (
                    <SelectItem key={opt} value={opt} className="text-[12px]"
                      data-testid={`${testidPrefix}-opt-${i}-${opt.toLowerCase().replace(/\s+/g,'-')}`}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input type="number" value={it.amount}
              onChange={(e) => update(i, "amount", e.target.value)}
              placeholder="Nominal"
              className="rounded-sm h-8 text-[11px] font-mono w-28"
              data-testid={`${testidPrefix}-amount-${i}`}/>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}
              className="h-7 w-7 p-0 rounded-sm text-zinc-400 hover:text-red-600"
              data-testid={`${testidPrefix}-remove-${i}`}>
              <Trash2 className="w-3.5 h-3.5"/>
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
